import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { deriveQuantumVacuumPresentationSignal } from '@inyron/living-universe'
import { fighters } from './content'
import { deriveRealityFrame } from './realityMath'
import { useFightStore } from './store'
import { fightLivingUniverse } from './livingUniverse'

export function deriveFightQuantumVacuumStrength(input: {
  playerQuantum: number
  enemyQuantum: number
  phase: 'neutral' | 'pressure' | 'climax' | 'collapse'
  status: string
  specialActive: boolean
}) {
  const quantum = Math.max(input.playerQuantum, input.enemyQuantum)
  if (input.status === 'match-over') return 1
  if (input.status === 'round-over') return Math.max(.72, quantum)
  if (input.phase === 'collapse') return Math.max(.78, quantum)
  if (input.specialActive && input.phase === 'climax') return Math.max(.56, quantum)
  return Math.min(.92, quantum)
}

/**
 * Presentation-only visualization of the combat quantum-vacuum state.
 * The deterministic simulation remains the sole authority for quantumTimer,
 * collision bypass, stamina cost and cooldown.
 */
export function QuantumVacuumPresentationField() {
  const root = useRef<THREE.Group>(null)
  const core = useRef<THREE.Mesh>(null)
  const rings = useRef<Array<THREE.Mesh | null>>(Array(5).fill(null))
  const veil = useRef<THREE.Mesh>(null)
  const colorA = useMemo(() => new THREE.Color(), [])
  const colorB = useMemo(() => new THREE.Color(), [])
  const mixed = useMemo(() => new THREE.Color(), [])

  useFrame(({ clock, camera }, dt) => {
    const s = useFightStore.getState()
    const frame = deriveRealityFrame(s.player, s.enemy, s.impactCue?.kind, s.impactCue?.attackKind)
    const specialActive =
      (s.player.lastAttack === 'special' && s.player.attackPhase !== null)
      || (s.enemy.lastAttack === 'special' && s.enemy.attackPhase !== null)
    const localStrength = deriveFightQuantumVacuumStrength({
      playerQuantum: Math.min(1, s.player.quantumTimer / .65),
      enemyQuantum: Math.min(1, s.enemy.quantumTimer / .65),
      phase: frame.phase,
      status: s.status,
      specialActive,
    })

    const centerX = (s.player.x + s.enemy.x) * .5
    const centerZ = (s.player.z + s.enemy.z) * .5
    const centerY = 1.05 + Math.max(s.player.y, s.enemy.y) * .32
    const localMemory = Math.min(1, s.impactSerial / 18 + (s.player.comboCount + s.enemy.comboCount) / 28)
    const localTurbulence = Math.min(1, frame.intensity * .62 + (Math.abs(s.player.x - s.enemy.x) < 2 ? .18 : 0))
    const localPulse = Math.min(1, frame.cinematic * .72 + (specialActive ? .28 : 0))
    const vacuumState = fightLivingUniverse.vacuumState()
    const vacuumCell = fightLivingUniverse.vacuumCellAt({ x: centerX, y: centerZ, z: 0 })
    const vacuumSignal = deriveQuantumVacuumPresentationSignal(vacuumState, vacuumCell)
    const memory = Math.max(localMemory, vacuumSignal.memory)
    const turbulence = Math.max(localTurbulence, vacuumSignal.turbulence * .8)
    const pulse = Math.max(localPulse, vacuumSignal.pulse * .8)
    const strength = Math.min(1, Math.max(localStrength, pulse * .54, vacuumSignal.strength * .78))
    colorA.set(fighters[s.playerIndex].accent)
    colorB.set(fighters[s.enemyIndex].accent)
    mixed.copy(colorA).lerp(colorB, .5)

    if (root.current) {
      root.current.visible = strength > .025
      root.current.position.x = THREE.MathUtils.damp(root.current.position.x, centerX, 10, dt)
      root.current.position.y = THREE.MathUtils.damp(root.current.position.y, centerY, 10, dt)
      root.current.position.z = THREE.MathUtils.damp(root.current.position.z, centerZ, 10, dt)
      root.current.rotation.y += dt * (.08 + strength * .34 + turbulence * .16)
    }

    if (core.current) {
      const breathe = 1 + Math.sin(clock.elapsedTime * (4.2 + strength * 3 + pulse * 1.8)) * (.06 + pulse * .025)
      core.current.scale.setScalar((.42 + strength * .92) * breathe)
      const material = core.current.material as THREE.MeshBasicMaterial
      material.opacity = strength * (.22 + memory * .06)
      material.color.copy(mixed)
    }

    rings.current.forEach((ring, index) => {
      if (!ring) return
      const phase = index / Math.max(1, rings.current.length - 1)
      const wave = 1 + Math.sin(clock.elapsedTime * (2.2 + phase + pulse * .8) + index * 1.7) * (.055 + turbulence * .018)
      ring.scale.setScalar((.82 + index * .28 + strength * 1.1) * wave)
      ring.rotation.x = Math.PI / 2 + Math.sin(clock.elapsedTime * .33 + index) * .17
      ring.rotation.y = clock.elapsedTime * (index % 2 === 0 ? .14 : -.11) + index * .4
      const material = ring.material as THREE.MeshBasicMaterial
      material.opacity = strength * Math.max(.045, .24 - index * .035) * (1 + memory * .18)
      material.color.copy(index % 2 === 0 ? colorA : colorB)
    })

    if (veil.current) {
      veil.current.lookAt(camera.position)
      veil.current.scale.setScalar(2.5 + strength * 2.2 + memory * .45)
      const material = veil.current.material as THREE.MeshBasicMaterial
      material.opacity = strength * (.055 + turbulence * .018)
      material.color.copy(mixed)
    }
  })

  return (
    <group ref={root} visible={false} renderOrder={26}>
      <mesh ref={core}>
        <sphereGeometry args={[.72, 32, 20]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
      </mesh>

      {Array.from({ length: 5 }, (_, index) => (
        <mesh
          key={index}
          ref={(mesh) => { rings.current[index] = mesh }}
          rotation={[Math.PI / 2, 0, index * .38]}
        >
          <ringGeometry args={[.88 + index * .17, .91 + index * .17, 72]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} blending={THREE.AdditiveBlending} side={THREE.DoubleSide} toneMapped={false} />
        </mesh>
      ))}

      <mesh ref={veil} position={[0, 0, -.24]}>
        <planeGeometry args={[3.6, 3.6]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} depthTest={false} blending={THREE.AdditiveBlending} toneMapped={false} />
      </mesh>

      <pointLight color="#9fdfff" intensity={2.2} distance={9} decay={2} />
    </group>
  )
}

